import { Dropdown, useDropdownMenu, useDropdownToggle } from '@restart/ui'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { debounce, get } from 'lodash'
import { empty } from 'utils/empty'
import { joinStyles } from 'utils/joinStyles'
import { toString } from 'utils/toString'
import { Search, X } from 'lucide-react'
import { cn } from 'lib/utils'

export function AdvancedSelect({
    multiSelection,
    valueAsOption,
    portal,
    lazy,
    clearable,
    value,
    name = '',
    id = '',
    onChange,
    onFocus,
    onBlur,
    placeholder = 'Seleccionar',
    options,
    optionValue = 'value',
    optionLabel = 'label',
    optionTemplate,
    selectedTemplate,
    filterBy,
    filterType = 'start',
    filterPlaceholder,
    filterDelay = 300,
    onSearch,
    menuClassName,
    menuStyle,
    ...props
}) {
    const filterValueRef = useRef(null)
    const historySelected = useRef([])
    const [show, setShow] = useState(false)
    const [opts, setOpts] = useState([])
    const [hovered, setHovered] = useState(-1)
    const [filterValue, setFilterValue] = useState('')
    const { baseOpts, selectedMap, selected } = useMemo(() => {
        const selectedValues = Array.isArray(value) ? value : empty(value) ? [] : [value]
        const prevSelectedMap = {}
        // Verificar los seleccionados en el historial
        if (selectedValues.length && historySelected.current.length) {
            historySelected.current.forEach((opt) => {
                if (selectedValues.some((s) => getOptionProp(s, optionValue) === opt.value)) {
                    prevSelectedMap[opt.value] = opt.data
                }
            })
        }
        const { opts, selectedMap } = formatOptions(options, selectedValues, { optionValue, optionLabel })
        const sm = { ...prevSelectedMap, ...selectedMap }
        return { baseOpts: opts, selectedMap: sm, selected: Object.values(sm) }
    }, [options, optionValue, optionLabel, optionTemplate, value])
    useEffect(() => {
        if (!show && hovered >= 0) setHovered(-1)
        if (show && filterBy) filterValueRef.current?.focus()
    }, [show])
    useEffect(() => {
        if (lazy) {
            setOpts(baseOpts)
        } else {
            if (filterBy) {
                setOpts(filterOptions(baseOpts, filterValue, { filterBy, filterType }))
            } else {
                setOpts(baseOpts)
            }
        }
    }, [baseOpts, lazy, filterBy, filterType, filterValue])
    useEffect(() => {
        if (hovered >= 0) setHovered(-1)
    }, [filterValue])
    const handleSearch = useCallback(
        debounce((searchValue) => {
            if (lazy) onSearch?.(searchValue)
        }, filterDelay),
        [lazy, filterDelay],
    )
    const buildEvent = (opt) => {
        return {
            target: {
                name,
                id,
                value: valueAsOption
                    ? opt
                    : multiSelection
                      ? opt.map((o) => getOptionProp(o, optionValue))
                      : getOptionProp(opt, optionValue),
            },
        }
    }
    const handleSelectOption = (opt) => {
        if (filterBy && lazy) historySelected.current.push(opt)
        if (multiSelection) {
            let _values = []
            if (opt.selected) {
                for (const key in selectedMap) {
                    if (opt.value !== key) _values.push(selectedMap[key])
                }
            } else {
                _values = [...selected, opt.data]
            }
            onChange?.(buildEvent(_values))
        } else {
            if (!opt.selected) {
                onChange?.(buildEvent(opt.data))
            }
            setShow(false)
        }
    }
    const handleFocus = () => {
        onFocus?.(buildEvent(value))
    }
    const handleBlur = () => {
        onBlur?.(buildEvent(value))
    }
    const handleClear = (e) => {
        e.stopPropagation()
        onChange?.(buildEvent(multiSelection ? [] : { data: '', value: '' }))
    }
    const handleKeyDown = (e) => {
        if (show) {
            switch (e.key) {
                case 'Enter':
                    e.preventDefault()
                    break
                case 'ArrowUp':
                    const lastIndex = hovered - 1
                    if (lastIndex < 0) {
                        e.preventDefault()
                        setShow(false)
                    }
                    setHovered(lastIndex)
                    break
                case 'ArrowDown':
                    const nextIndex = hovered + 1
                    if (nextIndex < opts.length) {
                        setHovered(nextIndex)
                    }
                    break
                default:
                    break
            }
        }
    }
    const handleKeyUp = (e) => {
        if (show) {
            switch (e.key) {
                case 'Enter':
                    if (hovered >= 0) {
                        handleSelectOption(opts[hovered])
                    }
                    break
                default:
                    break
            }
        }
    }
    const handleChangeFilterValue = (e) => {
        setFilterValue(e.target.value)
        handleSearch(e.target.value)
    }
    const headerMenu = filterBy && (
        <div className='mod-as-filter'>
            <Search />
            <input
                ref={filterValueRef}
                value={filterValue}
                onChange={handleChangeFilterValue}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                placeholder={filterPlaceholder}
            />
        </div>
    )
    return (
        <Dropdown show={show} onToggle={(next) => setShow(next)}>
            <AdvancedSelectField
                {...props}
                selected={selected}
                multiSelection={!!multiSelection}
                selectedTemplate={selectedTemplate}
                optionLabel={optionLabel}
                placeholder={placeholder}
                onClear={clearable ? handleClear : undefined}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
            />
            <AdvancedSelectMenu
                options={opts}
                optionTemplate={optionTemplate}
                multiSelection={!!multiSelection}
                hovered={hovered}
                portal={!!portal}
                onSelectOption={handleSelectOption}
                onFocus={handleFocus}
                onBlur={handleBlur}
                headerElement={headerMenu}
                className={menuClassName}
                style={menuStyle}
            />
        </Dropdown>
    )
}
function AdvancedSelectField({
    selected,
    multiSelection,
    selectedTemplate,
    optionLabel,
    placeholder,
    onClear,
    className,
    ...props
}) {
    const [toggleProps] = useDropdownToggle()
    return (
        <button
            {...props}
            type='button'
            ref={toggleProps.ref}
            onClick={toggleProps.onClick}
            className={cn('modular-advanced-select', className)}
            aria-expanded={toggleProps['aria-expanded']}
        >
            <div className='mod-asf-label'>
                {selectedTemplate?.(multiSelection ? selected : (selected[0] ?? '')) ??
                    (selected.map((s) => getOptionProp(s, optionLabel)).join(', ') || (placeholder ?? ''))}
            </div>
            {selected.length > 0 && onClear && (
                <div className='mod-asf-clear' onClick={onClear}>
                    <X />
                </div>
            )}
        </button>
    )
}
function AdvancedSelectMenu({
    options,
    portal,
    headerElement,
    footerElement,
    optionTemplate,
    hovered,
    multiSelection,
    onSelectOption,
    className,
    style,
    ...props
}) {
    const menuRef = useRef(null)
    const optionsRef = useRef([])
    const [{ style: menuStyle, ...menuProps }, { show, popper }] = useDropdownMenu({
        flip: true,
        offset: [0, 4],
    })
    useLayoutEffect(() => {
        if (show) popper?.update()
    }, [show])
    useEffect(() => {
        if (show && menuRef.current) {
            menuRef.current.scrollTop = 0
        }
    }, [show])
    useEffect(() => {
        if (hovered >= 0 && hovered < options.length) {
            optionsRef.current[hovered].scrollIntoView({ block: 'nearest' })
        }
    }, [hovered])
    const menuElement = (
        <div
            {...props}
            {...menuProps}
            className={cn('mod-as-menu', show && 'show', className)}
            style={joinStyles(menuStyle, style)}
            tabIndex={-1}
        >
            {headerElement && <div className='mod-asm-header'>{headerElement}</div>}
            <div ref={menuRef} className='mod-asm-body'>
                {options.length > 0 ? (
                    options.map((opt, index) => (
                        <div
                            key={opt.key}
                            ref={(el) => {
                                if (el) optionsRef.current[index] = el
                            }}
                            className={cn('mod-asm-option', {
                                selected: opt.selected,
                                hovered: hovered === index,
                            })}
                            onClick={onSelectOption ? () => onSelectOption(opt) : undefined}
                        >
                            {multiSelection && (
                                <div className='mod-asmo-checkbox'>
                                    <input
                                        type='checkbox'
                                        checked={opt.selected}
                                        onChange={() => {}}
                                        className='form-check-input'
                                        tabIndex={-1}
                                    />
                                </div>
                            )}
                            <div className='mod-asmo-label'>
                                {optionTemplate?.({
                                    data: opt.data,
                                    value: opt.value,
                                    index,
                                    selected: opt.selected,
                                }) ?? opt.label}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className='mod-asm-empty-option'>Sin opciones</div>
                )}
            </div>
            {footerElement && <div className='mod-asm-footer'>{footerElement}</div>}
        </div>
    )
    return portal ? createPortal(menuElement, document.body) : menuElement
}
function formatOptions(options, selectedValues, props) {
    const selectedMap = {}
    const opts = options.map((opt) => {
        const value = getOptionProp(opt, props.optionValue)
        const selected = selectedValues.some((s) => getOptionProp(s, props.optionValue) === value)
        const label = getOptionProp(opt, props.optionLabel)
        if (selected) selectedMap[value] = opt
        return {
            data: opt,
            key: value,
            value,
            label,
            selected,
        }
    })
    return { opts, selectedMap }
}
function getOptionProp(option, optionProp) {
    if (typeof option === 'object' && option !== null) {
        const _prop = get(option, optionProp)
        return toString(_prop)
    } else {
        return toString(option ?? '')
    }
}
function filterOptions(opts, filterValue, props) {
    return opts.filter((opt) =>
        props.filterType === 'contain'
            ? contains(getOptionProp(opt.data, props.filterBy), filterValue)
            : startsWith(getOptionProp(opt.data, props.filterBy), filterValue),
    )
}
function startsWith(str, prefix) {
    return toLowerCase(str).startsWith(toLowerCase(prefix))
}
function contains(str, needle) {
    return toLowerCase(str).includes(toLowerCase(needle))
}
function toLowerCase(str) {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
}
